/**
 * Voice-note waveforms as WhatsApp draws them: bars from the sound itself, silence as dots.
 * A received or sent note is read once from its file and kept for the session; a recording
 * gets its levels from the microphone as it goes.
 */

/** Loudness per bar (0..1, the loudest bar is 1) from raw samples. */
export function levelsFromSamples(
  samples: ArrayLike<number>,
  bars: number,
): number[] {
  const size = Math.max(1, Math.floor(samples.length / bars));
  const out: number[] = [];
  for (let b = 0; b < bars; b++) {
    const start = b * size;
    const end = Math.min(samples.length, start + size);
    let sum = 0;
    for (let i = start; i < end; i++) sum += samples[i]! * samples[i]!;
    out.push(end > start ? Math.sqrt(sum / (end - start)) : 0);
  }
  const max = Math.max(...out, 1e-6);
  return out.map((v) => v / max);
}

/** Many levels into a fixed number of bars (a recording of any length into the bar). */
export function bucket(levels: number[], bars: number): number[] {
  if (!levels.length) return [];
  if (levels.length <= bars) return levels;
  return Array.from({ length: bars }, (_, i) => {
    const from = Math.floor((i * levels.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / bars));
    return Math.max(...levels.slice(from, to));
  });
}

/** A bar's height in pixels, or null for silence, which WhatsApp draws as a dot. */
export function barHeight(level: number, max = 24): number | null {
  if (level < 0.1) return null;
  return Math.max(4, Math.round(level * max));
}

const cache = new Map<string, Promise<number[] | null>>();

/** The waveform of a voice note's file; null when the browser cannot read it. */
export function voiceLevels(
  src: string,
  bars: number,
): Promise<number[] | null> {
  const key = `${src}#${bars}`;
  let levels = cache.get(key);
  if (!levels) {
    levels = (async () => {
      try {
        const res = await fetch(src, { credentials: "same-origin" });
        if (!res.ok) return null;
        const data = await res.arrayBuffer();
        const ctx = new OfflineAudioContext(1, 1, 44100);
        const audio = await ctx.decodeAudioData(data);
        return levelsFromSamples(audio.getChannelData(0), bars);
      } catch {
        return null;
      }
    })();
    cache.set(key, levels);
  }
  return levels;
}
